/**
 * SuggestionChipRow Component
 *
 * Shared primitive for rendering a wrapping row of suggestion/quick-reply
 * chips. Extracted in Batch 2 so QuickReplyButtons (follow-ups on a message)
 * and SuggestionChips (page-context starters) no longer duplicate the chip
 * markup, wrap styling, and the "+N more" collapse behaviour.
 *
 * DRY / Rule 15: single source of truth for chip presentation. Import from
 * @axon/ui only.
 *
 * @module SuggestionChipRow
 */

'use client';

import { memo, useState } from 'react';
import { Flex, Button } from '@axon/ui';
import { useTranslations } from 'next-intl';

// ============================================================================
// Constants
// ============================================================================

/** Default visible chips before the remainder collapses behind a "+N more" chip. */
const DEFAULT_VISIBLE_CAP = 4;

/**
 * Shared inline style for chips: wrap instead of overflowing the panel, grow
 * to fit multi-line labels, and left-align text. The @axon/ui Button extends
 * ButtonHTMLAttributes (not a Chakra style-prop component), so layout that
 * Chakra props can't express goes through the HTML `style` prop.
 */
const CHIP_STYLE: React.CSSProperties = {
  whiteSpace: 'normal',
  height: 'auto',
  paddingTop: '0.375rem',
  paddingBottom: '0.375rem',
  textAlign: 'left',
  maxWidth: '100%',
};

// ============================================================================
// Types
// ============================================================================

export interface SuggestionChip {
  /** Stable React key. */
  id: string;
  /** Visible chip text. */
  label: string;
  /** Value passed to onSelect when the chip is clicked. */
  value: string;
}

interface SuggestionChipRowProps {
  chips: SuggestionChip[];
  onSelect: (value: string) => void;
  disabled?: boolean;
  /** Max chips shown before collapsing behind "+N more". 0 disables the cap. */
  cap?: number;
}

// ============================================================================
// Component
// ============================================================================

/**
 * SuggestionChipRow component
 *
 * @example
 * <SuggestionChipRow
 *   chips={[{ id: '1', label: 'Show details', value: 'Show details' }]}
 *   onSelect={handleSelect}
 * />
 */
export const SuggestionChipRow = memo(function SuggestionChipRow({
  chips,
  onSelect,
  disabled = false,
  cap = DEFAULT_VISIBLE_CAP,
}: SuggestionChipRowProps) {
  const t = useTranslations('chatbot');
  const [isExpanded, setIsExpanded] = useState(false);

  if (chips.length === 0) return null;

  const hasOverflow = cap > 0 && chips.length > cap;
  const visibleChips = hasOverflow && !isExpanded ? chips.slice(0, cap) : chips;
  const hiddenCount = chips.length - cap;

  return (
    <Flex gap="2" flexWrap="wrap" marginTop="2">
      {visibleChips.map((chip) => (
        <Button
          key={chip.id}
          size="sm"
          variant="outline"
          disabled={disabled}
          style={CHIP_STYLE}
          onClick={() => !disabled && onSelect(chip.value)}
        >
          {chip.label}
        </Button>
      ))}
      {hasOverflow && !isExpanded && (
        <Button
          key="__more__"
          size="sm"
          variant="ghost"
          disabled={disabled}
          style={CHIP_STYLE}
          onClick={() => setIsExpanded(true)}
        >
          {t('suggestions.more' as Parameters<typeof t>[0], { count: hiddenCount })}
        </Button>
      )}
    </Flex>
  );
});

export default SuggestionChipRow;
